import { dayHeading } from '../crm/contactDetail/contactDetail';

export type ActivityKind = 'ACTION' | 'APP' | 'MESSAGE';

export interface ActivityEntry {
  id: string;
  kind: ActivityKind;
  type: string;
  occurredAt: string;
  title: string;
  body?: string | null;
  note?: string | null;
  businessName?: string | null;
  businessPlaceId?: string | null;
  otherUserId?: number | null;
  otherUserName?: string | null;
  screen?: string | null;
}

export const ACTIVITY_KINDS: Array<{ kind: ActivityKind; label: string }> = [
  { kind: 'ACTION', label: 'What they did' },
  { kind: 'APP', label: 'Using the app' },
  { kind: 'MESSAGE', label: 'What we sent them' },
];

export const USER_TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'recommendations', label: 'Recommendations' },
  { key: 'activity', label: 'Activity' },
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'reviews', label: 'Reviews' },
  { key: 'referrals', label: 'Referrals' },
  { key: 'badges', label: 'Badges' },
  { key: 'points', label: 'Points' },
] as const;

export type UserTab = typeof USER_TABS[number]['key'];

export const parseTab = (value: string | null): UserTab =>
  USER_TABS.find((tab) => tab.key === value)?.key ?? 'overview';

/** Strict, as `parseId` in crmPaths.ts: `Number('12abc')` is NaN, `parseInt` would open user 12. */
export const parseUserId = (value: string | undefined): number | null =>
  value && /^[1-9]\d{0,9}$/.test(value) && Number(value) <= 2147483647 ? Number(value) : null;

/** What an attachment on a WhatsApp message is, in a word. */
export const attachmentLabel = (contentType?: string | null) => {
  const kind = contentType?.split('/')[0];
  if (kind === 'image') return 'Photo';
  if (kind === 'video') return 'Video';
  if (kind === 'audio') return 'Voice note';
  return 'Attachment';
};

export const nameOf = (user: { firstName?: string | null; lastName?: string | null; username?: string | null }) =>
  [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.username || 'No name';

export const initialsOf = (user: { firstName?: string | null; lastName?: string | null; username?: string | null }) => {
  const initials = [user.firstName, user.lastName]
    .map((part) => part?.trim()[0])
    .filter(Boolean)
    .join('');
  return (initials || user.username?.[0] || '?').toUpperCase();
};

export interface AccountChip {
  label: string;
  color: 'default' | 'error' | 'warning' | 'success' | 'primary';
}

/**
 * The states worth seeing at a glance. A ban also sets `isDeleted`, so a banned
 * account says Banned and not Deleted as well; "Unverified" is the gate on
 * recommending and joining a chat group (docs/UNVERIFIED_USERS.md in the API).
 */
export function accountChips(user: {
  isBanned?: boolean;
  isDeleted?: boolean;
  isVerified?: boolean;
  role?: string;
  ambassador?: boolean;
  membershipType?: string;
}): AccountChip[] {
  const chips: AccountChip[] = [];
  if (user.isBanned) chips.push({ label: 'Banned', color: 'error' });
  else if (user.isDeleted) chips.push({ label: 'Deleted', color: 'error' });
  chips.push(user.isVerified ? { label: 'Verified', color: 'success' } : { label: 'Unverified', color: 'warning' });
  if (user.role === 'ADMIN') chips.push({ label: 'Admin', color: 'primary' });
  if (user.ambassador) chips.push({ label: 'Ambassador', color: 'primary' });
  if (user.membershipType && user.membershipType !== 'FREE') {
    chips.push({ label: humanize(user.membershipType), color: 'default' });
  }
  return chips;
}

/** `FRIEND_OR_FAMILY` as "Friend or family". */
export const humanize = (value: string | null | undefined): string => {
  const words = (value ?? '').replace(/[_-]+/g, ' ').trim().toLowerCase();
  return words ? words[0].toUpperCase() + words.slice(1) : '';
};

/** `livestream:Marketing_12ab` as the group's name. */
export const communityName = (channel: string) => channel.replace(/^[^:]*:/, '').split('_')[0];

export const locationText = (user: {
  regionCity?: string | null;
  regionState?: string | null;
  regionCountry?: string | null;
}) => [user.regionCity, user.regionState, user.regionCountry].filter(Boolean).join(', ');

/** English when there is one, else whichever exists: older initiatives may have a single translation. */
export const initiativeTitle = (translations: Array<{ locale: string; title: string }> | null | undefined) =>
  translations?.find((t) => t.locale.startsWith('en'))?.title ?? translations?.[0]?.title ?? 'Untitled badge';

/** One row of the feed: an entry, or a run of screen views folded into one line. */
export type ActivityRow =
  | { row: 'entry'; id: string; entry: ActivityEntry }
  | { row: 'screens'; id: string; occurredAt: string; endedAt: string; screens: string[] };

/**
 * Folds neighbouring screen views. Someone moving around the app logs a row per
 * screen, and thirty of those between two things they did would bury both.
 */
export function foldScreens(items: ReadonlyArray<ActivityEntry>): ActivityRow[] {
  const rows: ActivityRow[] = [];
  items.forEach((entry) => {
    const last = rows[rows.length - 1];
    if (entry.type !== 'screen_view') {
      rows.push({ row: 'entry', id: entry.id, entry });
    } else if (last?.row === 'screens' && dayHeading(last.occurredAt) === dayHeading(entry.occurredAt)) {
      // Newest first: the entry being added is the earlier one.
      last.occurredAt = entry.occurredAt;
      last.screens.unshift(entry.screen ?? entry.title);
    } else {
      rows.push({
        row: 'screens',
        id: entry.id,
        occurredAt: entry.occurredAt,
        endedAt: entry.occurredAt,
        screens: [entry.screen ?? entry.title],
      });
    }
  });
  return rows;
}

const rowTime = (row: ActivityRow) => (row.row === 'entry' ? row.entry.occurredAt : row.endedAt);

/** Rows under a heading per day, in the order given. */
export function activityByDay(rows: ReadonlyArray<ActivityRow>, heading = dayHeading) {
  const days: Array<{ heading: string; rows: ActivityRow[] }> = [];
  rows.forEach((row) => {
    const name = heading(rowTime(row));
    const last = days[days.length - 1];
    if (last?.heading === name) last.rows.push(row);
    else days.push({ heading: name, rows: [row] });
  });
  return days;
}

/** "Settings → Edit profile", leaving out a screen repeated straight after itself. */
export const screenTrail = (screens: ReadonlyArray<string>) =>
  screens.filter((screen, index) => screen !== screens[index - 1]).join(' → ');

/** "1 screen", "7 screens". */
export const countOf = (n: number, unit: string) => `${n.toLocaleString('en-US')} ${unit}${n === 1 ? '' : 's'}`;
